import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle, XCircle, Eye, Trash2, User, MessageSquare, FileAudio, RefreshCw, ArrowLeft, History, Image as ImageIcon, Video, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import AdminReportsAuditPanel from '../components/AdminReportsAuditPanel';

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, reviewed, resolved, dismissed
  const [selectedReport, setSelectedReport] = useState(null);
  const [auditReportId, setAuditReportId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadReports();
  }, [filter]);

  const loadReports = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await axios.get('/api/admin/reports', { params });
      setReports(response.data);
    } catch (error) {
      console.error('Error loading reports:', error);
      if (error.response?.status === 403) {
        toast.error('Accès réservé aux administrateurs');
        navigate('/feed');
      } else {
        toast.error('Erreur lors du chargement des signalements');
      }
    } finally {
      setLoading(false);
    }
  };

  const [resolveDialog, setResolveDialog] = useState(null); // { reportId, status, actionTaken }
  const [resolveNote, setResolveNote] = useState('');
  const [resolving, setResolving] = useState(false);

  const openResolveDialog = (reportId, status, actionTaken = null) => {
    setResolveDialog({ reportId, status, actionTaken });
    setResolveNote('');
  };

  const handleUpdateReport = async (reportId, status, actionTaken = null) => {
    // Route through the mandatory-note dialog
    openResolveDialog(reportId, status, actionTaken);
  };

  const confirmResolveWithNote = async () => {
    if (!resolveDialog) return;
    const clean = resolveNote.trim();
    if (clean.length < 3) {
      toast.error('Une note d\'au moins 3 caractères est obligatoire');
      return;
    }
    setResolving(true);
    try {
      // 1) Log the note in the audit trail (records handler name/date server-side)
      await axios.post(`/api/admin/reports-audit/content/${resolveDialog.reportId}/note`, {
        note: clean,
        action: resolveDialog.actionTaken || resolveDialog.status,
      });
      // 2) Update the report status
      await axios.put(`/api/admin/reports/${resolveDialog.reportId}`, null, {
        params: { status: resolveDialog.status, action_taken: resolveDialog.actionTaken },
      });
      toast.success('Signalement traité et note enregistrée');
      setResolveDialog(null);
      setResolveNote('');
      loadReports();
      setSelectedReport(null);
    } catch (error) {
      console.error('Error updating report:', error);
      toast.error(error?.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setResolving(false);
    }
  };

  const handleDeleteContent = async (contentType, contentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contenu ?')) {
      return;
    }

    try {
      if (contentType === 'gim_toli') {
        await axios.delete(`/api/admin/content/posts/${contentId}`);
      } else if (contentType === 'podcast') {
        await axios.delete(`/api/admin/content/podcasts/${contentId}`);
      } else if (contentType === 'user') {
        await axios.delete(`/api/admin/users/${contentId}`);
      } else if (contentType === 'article') {
        await axios.delete(`/api/explorer/articles/${contentId}`);
      } else if (contentType === 'photo') {
        await axios.delete(`/api/admin/photos/${contentId}`);
      } else if (contentType === 'video') {
        await axios.delete(`/api/admin/videos/${contentId}`);
      }
      
      toast.success('Contenu supprimé avec succès');
      
      // Update report as resolved
      if (selectedReport) {
        await handleUpdateReport(selectedReport.id, 'resolved', 'content_deleted');
      }
    } catch (error) {
      console.error('Error deleting content:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const getContentTypeIcon = (type) => {
    switch (type) {
      case 'gim_toli': return <FileAudio className="w-5 h-5" />;
      case 'podcast': return <FileAudio className="w-5 h-5" />;
      case 'studio_story': return <FileAudio className="w-5 h-5" />; // legacy report — kept for backwards-compat display
      case 'photo': return <ImageIcon className="w-5 h-5" />;
      case 'video': return <Video className="w-5 h-5" />;
      case 'ai_content': return <Sparkles className="w-5 h-5" />;
      case 'comment': return <MessageSquare className="w-5 h-5" />;
      case 'user': return <User className="w-5 h-5" />;
      case 'article': return <MessageSquare className="w-5 h-5" />;
      default: return <AlertTriangle className="w-5 h-5" />;
    }
  };

  const getContentTypeName = (type) => {
    switch (type) {
      case 'gim_toli': return 'Gim Toli';
      case 'podcast': return 'Podcast';
      case 'studio_story': return 'Histoire audio';
      case 'photo': return 'Photo partagée';
      case 'video': return 'Vidéo partagée';
      case 'ai_content': return 'Contenu généré par IA';
      case 'story': return 'Story 24h';
      case 'comment': return 'Commentaire';
      case 'user': return 'Utilisateur';
      case 'article': return 'Article (Actus)';
      case 'short_film': return 'Film court';
      default: return type;
    }
  };

  const getReasonLabel = (reason) => {
    const reasons = {
      spam: 'Spam ou contenu indésirable',
      inappropriate: 'Contenu inapproprié',
      violence: 'Violence ou contenu dangereux',
      harassment: 'Harcèlement',
      misinformation: 'Fausses informations',
      copyright: 'Violation de droits d\'auteur',
      other: 'Autre'
    };
    return reasons[reason] || reason;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: 'En attente' },
      reviewed: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: 'Examiné' },
      resolved: { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: 'Résolu' },
      dismissed: { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', label: 'Rejeté' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin text-magenta-500" />
          <p className="text-gray-400">Chargement des signalements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-black/90 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                title="Retour au dashboard"
              >
                <ArrowLeft className="w-6 h-6 text-gray-400 hover:text-magenta-500 transition-colors" />
              </button>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-magenta-500 to-violet-600 bg-clip-text text-transparent">
                  Modération - Signalements
                </h1>
                <p className="text-gray-400 mt-1">Gérer les signalements de contenu</p>
              </div>
            </div>
            <button
              onClick={loadReports}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
              title="Actualiser"
            >
              <RefreshCw className="w-6 h-6 text-gray-400 hover:text-magenta-500 transition-colors" />
            </button>
          </div>

          {/* Quick link to group reports */}
          <button
            onClick={() => navigate('/admin/group-reports')}
            className="mt-4 w-full text-left bg-gradient-to-r from-violet-600/20 to-violet-500/10 border border-violet-500/40 rounded-xl p-4 hover:from-violet-600/30 hover:to-violet-500/20 transition-all flex items-center justify-between"
            data-testid="open-group-reports-btn"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-violet-500/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-violet-300" />
              </div>
              <div>
                <p className="font-semibold">Signalements de groupes</p>
                <p className="text-xs text-gray-400">Voir les groupes et messages signalés</p>
              </div>
            </div>
            <span className="text-violet-300">→</span>
          </button>

          {/* Audit trail + archives (managers + super admin) */}
          <div className="mt-4">
            <AdminReportsAuditPanel
              kind="content"
              selectedReportId={auditReportId}
              onCloseSelected={() => setAuditReportId(null)}
            />
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-2 mt-6 overflow-x-auto pb-2">
            {[
              { value: 'pending', label: 'En attente', count: reports.filter(r => r.status === 'pending').length },
              { value: 'reviewed', label: 'Examinés', count: reports.filter(r => r.status === 'reviewed').length },
              { value: 'resolved', label: 'Résolus', count: reports.filter(r => r.status === 'resolved').length },
              { value: 'dismissed', label: 'Rejetés', count: reports.filter(r => r.status === 'dismissed').length },
              { value: 'all', label: 'Tous', count: reports.length }
            ].map((tab) => (
              <button
                key={tab.value}
                onClick={() => setFilter(tab.value)}
                className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                  filter === tab.value
                    ? 'bg-gradient-to-r from-magenta-500 to-violet-600 text-white'
                    : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                {tab.label} {tab.count > 0 && `(${tab.count})`}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {reports.length === 0 ? (
          <div className="text-center py-20">
            <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400">Aucun signalement pour ce filtre</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {reports.map((report) => (
              <div
                key={report.id}
                className="bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-magenta-500/50 transition-all"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-red-500/20 rounded-lg">
                        {getContentTypeIcon(report.content_type)}
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-white">
                            {getContentTypeName(report.content_type)} signalé
                          </h3>
                          {getStatusBadge(report.status)}
                        </div>
                        <p className="text-sm text-gray-400 mt-1">
                          Par {report.reporter?.name || 'Utilisateur'} • {new Date(report.created_at).toLocaleDateString('fr-FR')} à {new Date(report.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Reason */}
                  <div className="mb-4">
                    <p className="text-sm text-gray-400 mb-1">Motif :</p>
                    <p className="text-white font-medium">{getReasonLabel(report.reason)}</p>
                  </div>

                  {/* Details */}
                  {report.details && (
                    <div className="mb-4">
                      <p className="text-sm text-gray-400 mb-1">Détails :</p>
                      <p className="text-white bg-white/5 p-3 rounded-lg">{report.details}</p>
                    </div>
                  )}

                  {/* Content Preview */}
                  {report.content && (
                    <div className="mb-4 p-4 bg-white/5 rounded-lg border border-white/10">
                      <p className="text-sm text-gray-400 mb-2">Contenu signalé :</p>
                      {report.content_type === 'gim_toli' && (
                        <div>
                          <p className="text-white font-medium">{report.content.title || 'Sans titre'}</p>
                          <p className="text-sm text-gray-400 mt-1">
                            Par {report.content.user?.name || 'Utilisateur'}
                          </p>
                        </div>
                      )}
                      {(report.content_type === 'photo' || report.content_type === 'video') && report.media_preview && (
                        <img src={report.media_preview.startsWith('http') ? report.media_preview : `${process.env.REACT_APP_BACKEND_URL || ''}${report.media_preview}`} alt="photo signalée" className="w-24 h-24 object-cover rounded-lg" />
                      )}
                      {report.content_type === 'ai_content' && (
                        <div data-testid="report-ai-content-preview">
                          <p className="text-xs text-fuchsia-300 mb-1">Fonction IA : {report.ai_feature || 'ia'}</p>
                          {report.ai_prompt && <p className="text-sm text-gray-300 mb-1"><span className="text-gray-500">Prompt :</span> {report.ai_prompt}</p>}
                          {typeof report.content === 'string' && <p className="text-white text-sm whitespace-pre-wrap">{report.content}</p>}
                          {report.media_preview && (
                            report.media_preview.match(/\.(mp4|mov|webm)(\?|$)/i)
                              ? <video src={report.media_preview.startsWith('http') ? report.media_preview : `${process.env.REACT_APP_BACKEND_URL || ''}${report.media_preview}`} controls className="mt-2 w-48 rounded-lg bg-black" />
                              : <img src={report.media_preview.startsWith('http') ? report.media_preview : `${process.env.REACT_APP_BACKEND_URL || ''}${report.media_preview}`} alt="contenu IA signalé" className="mt-2 w-32 h-32 object-cover rounded-lg" />
                          )}
                        </div>
                      )}
                      {report.content_type === 'user' && (
                        <div>
                          <p className="text-white font-medium">{report.content.name}</p>
                          <p className="text-sm text-gray-400">@{report.content.username}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-end space-x-2 pt-4 border-t border-white/10 flex-wrap gap-2">
                    <button
                      onClick={() => setAuditReportId(report.id)}
                      className="px-3 py-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 rounded-lg transition-colors flex items-center space-x-2 text-sm"
                      data-testid={`open-history-${report.id}`}
                      title="Voir l'historique et ajouter une note"
                    >
                      <History className="w-4 h-4" />
                      <span>Historique</span>
                    </button>
                    {report.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleUpdateReport(report.id, 'dismissed', 'no_action_needed')}
                          className="px-4 py-2 bg-gray-500/20 hover:bg-gray-500/30 text-gray-300 rounded-lg transition-colors flex items-center space-x-2"
                        >
                          <XCircle className="w-4 h-4" />
                          <span>Rejeter</span>
                        </button>
                        
                        <button
                          onClick={() => handleDeleteContent(report.content_type, report.content_id)}
                          className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors flex items-center space-x-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Supprimer le contenu</span>
                        </button>
                        
                        <button
                          onClick={() => handleUpdateReport(report.id, 'reviewed', 'under_review')}
                          className="px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors flex items-center space-x-2"
                        >
                          <Eye className="w-4 h-4" />
                          <span>Marquer comme examiné</span>
                        </button>
                      </>
                    )}
                    
                    {report.status === 'reviewed' && (
                      <>
                        <button
                          onClick={() => handleUpdateReport(report.id, 'dismissed', 'no_action_needed')}
                          className="px-4 py-2 bg-gray-500/20 hover:bg-gray-500/30 text-gray-300 rounded-lg transition-colors flex items-center space-x-2"
                        >
                          <XCircle className="w-4 h-4" />
                          <span>Rejeter</span>
                        </button>
                        
                        <button
                          onClick={() => handleUpdateReport(report.id, 'resolved', 'action_taken')}
                          className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg transition-colors flex items-center space-x-2"
                        >
                          <CheckCircle className="w-4 h-4" />
                          <span>Marquer comme résolu</span>
                        </button>
                      </>
                    )}
                    
                    {(report.status === 'resolved' || report.status === 'dismissed') && (
                      <div className="text-sm text-gray-400">
                        {report.reviewed_at && (
                          <>
                            Traité le {new Date(report.reviewed_at).toLocaleDateString('fr-FR')}
                            {report.action_taken && ` • ${report.action_taken}`}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mandatory-note dialog when resolving/dismissing a report */}
      {resolveDialog && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="resolve-note-modal">
          <div className="bg-gray-900 border border-white/10 rounded-2xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-bold">Marquer ce signalement comme {resolveDialog.status === 'resolved' ? 'traité' : resolveDialog.status === 'dismissed' ? 'rejeté' : resolveDialog.status}</h3>
            <p className="text-sm text-gray-400">
              Merci de laisser une note pour indiquer <strong className="text-white">quelles actions ont été prises</strong>. Votre nom, la date et cette note seront enregistrés pour audit.
            </p>
            <textarea
              value={resolveNote}
              onChange={(e) => setResolveNote(e.target.value)}
              rows={4}
              autoFocus
              placeholder="Ex: Contenu supprimé, utilisateur averti par email, aucune action nécessaire, etc."
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-magenta-500 text-sm"
              data-testid="resolve-note-textarea"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setResolveDialog(null); setResolveNote(''); }}
                className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm"
                data-testid="resolve-note-cancel"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={confirmResolveWithNote}
                disabled={resolving || resolveNote.trim().length < 3}
                className="px-4 py-2 rounded-full bg-gradient-to-r from-magenta-500 to-violet-600 text-white text-sm font-semibold disabled:opacity-50"
                data-testid="resolve-note-confirm"
              >
                {resolving ? 'Envoi…' : 'Confirmer et enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Navigation />
    </div>
  );
};

export default Reports;
